import React, { useState } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert } from 'react-native';
import AppTextInput from './AppTextInput';
import type { StaffRole } from '../types/staff';
import { permissionsForRole } from '../types/staff';
import staffInvitationService from '../services/staffInvitationService';

interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  specialties: string;
}

interface ManageWorkersModalProps {
  visible: boolean;
  onClose: () => void;
  workers: Worker[];
  onAddWorker: (worker: Omit<Worker, 'id'>) => Promise<Worker>;
  onDeleteWorker: (workerId: string) => void;
  businessId: string;
  businessName: string;
  invitedByUserId: string;
}

const ManageWorkersModal: React.FC<ManageWorkersModalProps> = ({
  visible,
  onClose,
  workers,
  onAddWorker,
  onDeleteWorker,
  businessId,
  businessName,
  invitedByUserId,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [role, setRole] = useState<StaffRole>('worker');
  const [isInviting, setIsInviting] = useState(false);

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setSpecialties('');
    setRole('worker');
  };

  const handleAddWorkerOnly = async () => {
    if (!name.trim() || !phone.trim() || !email.trim()) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    try {
      await onAddWorker({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        specialties: specialties.trim(),
      });
      resetForm();
      Alert.alert('Success', 'Worker added successfully!');
    } catch (e) {
      Alert.alert('Error', 'Failed to add worker. Please try again.');
    }
  };

  const handleSaveAndInvite = async () => {
    if (!name.trim() || !phone.trim() || !email.trim()) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }
    if (!phone.trim() && !email.trim()) {
      Alert.alert('Error', 'Enter a phone number or email to send an invite.');
      return;
    }
    setIsInviting(true);
    try {
      const newWorker = await onAddWorker({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        specialties: specialties.trim(),
      });

      const invite = await staffInvitationService.createInvite({
        businessId,
        businessName,
        invitedByUserId,
        email: email.trim(),
        phone: phone.trim(),
        role,
        permissions: permissionsForRole(role),
        workerId: role === 'worker' ? newWorker.id : undefined,
      });

      const sent = await staffInvitationService.sendInvite(invite, businessName);
      resetForm();
      Alert.alert(
        'Invite sent',
        `Invite link created.\n\nSMS: ${sent.smsSent ? 'sent' : 'not sent'}\nEmail: ${sent.emailSent ? 'sent' : 'not sent'}`
      );
    } catch (e) {
      console.error('Invite error:', e);
      Alert.alert('Error', 'Could not send invite. The worker may still have been added.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteWorker = (worker: Worker) => {
    Alert.alert(
      'Delete Worker',
      `Are you sure you want to delete ${worker.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onDeleteWorker(worker.id),
        },
      ]
    );
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Manage Resources</Text>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content}>
          {/* Add Worker Form */}
          <View style={styles.addWorkerSection}>
            <Text style={styles.sectionTitle}>Add New Worker</Text>
            
            <AppTextInput
              style={styles.input}
              placeholder="Full Name *"
              value={name}
              onChangeText={setName}
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Phone Number *"
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Email Address *"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            
            <AppTextInput
              style={styles.input}
              placeholder="Specialties (e.g., Hair Styling, Coloring)"
              value={specialties}
              onChangeText={setSpecialties}
            />

            <View style={styles.roleRow}>
              <Text style={styles.roleLabel}>Role</Text>
              <View style={styles.roleButtons}>
                <TouchableOpacity
                  style={[styles.roleButton, role === 'worker' && styles.roleButtonActive]}
                  onPress={() => setRole('worker')}
                >
                  <Text style={[styles.roleButtonText, role === 'worker' && styles.roleButtonTextActive]}>Worker</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.roleButton, role === 'site_manager' && styles.roleButtonActive]}
                  onPress={() => setRole('site_manager')}
                >
                  <Text style={[styles.roleButtonText, role === 'site_manager' && styles.roleButtonTextActive]}>Site Manager</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.actionRow}>
              <TouchableOpacity style={[styles.addButton, styles.secondaryAction]} onPress={handleAddWorkerOnly}>
                <Text style={[styles.addButtonText, styles.secondaryActionText]}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.addButton, isInviting && styles.disabledButton]}
                onPress={handleSaveAndInvite}
                disabled={isInviting}
              >
                <Text style={styles.addButtonText}>{isInviting ? 'Sending…' : 'Save & Invite'}</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Workers List */}
          <View style={styles.workersSection}>
            <Text style={styles.sectionTitle}>Current Workers ({workers.length})</Text>
            
            {workers.length === 0 ? (
              <Text style={styles.noWorkersText}>No workers added yet</Text>
            ) : (
              workers.map((worker) => (
                <View key={worker.id} style={styles.workerCard}>
                  <View style={styles.workerInfo}>
                    <Text style={styles.workerName}>{worker.name}</Text>
                    <Text style={styles.workerDetails}>📞 {worker.phone}</Text>
                    <Text style={styles.workerDetails}>📧 {worker.email}</Text>
                    {worker.specialties && (
                      <Text style={styles.workerDetails}>🎨 {worker.specialties}</Text>
                    )}
                  </View>
                  <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDeleteWorker(worker)}
                  >
                    <Text style={styles.deleteButtonText}>Delete</Text>
                  </TouchableOpacity>
                </View>
              ))
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  closeButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  addWorkerSection: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  input: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginBottom: 15,
    fontSize: 16,
  },
  addButton: {
    backgroundColor: '#28a745',
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
  },
  addButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  actionRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  secondaryAction: {
    backgroundColor: '#E5E5E7',
  },
  secondaryActionText: {
    color: '#1C1C1E',
  },
  disabledButton: {
    opacity: 0.7,
  },
  roleRow: {
    marginBottom: 12,
  },
  roleLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#333',
    marginBottom: 8,
  },
  roleButtons: {
    flexDirection: 'row',
  },
  roleButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
  },
  roleButtonActive: {
    borderColor: '#007bff',
    backgroundColor: '#E9F2FF',
  },
  roleButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#333',
  },
  roleButtonTextActive: {
    color: '#007bff',
  },
  workersSection: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  noWorkersText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  workerCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  workerInfo: {
    flex: 1,
  },
  workerName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  workerDetails: {
    fontSize: 14,
    color: '#666',
    marginBottom: 2,
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
  },
  deleteButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
});

export default ManageWorkersModal;